import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { TreePine, ArrowLeft, ScrollText } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import LanguageSelector from "../components/LanguageSelector";
import SnowLayer from "../components/SnowLayer";
import { StarField } from "../components/Cinematic";
import { LEGAL_LINKS } from "../i18n/legalLinks";

// Shared layout for public legal pages (Terms / Privacy). Indexable: sets document title + meta description per language.
export default function LegalPage({ copy, slug, testid }) {
  const { lang } = useI18n();
  const C = copy(lang);
  const L = LEGAL_LINKS[lang] || LEGAL_LINKS.pt;

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const prev = document.title;
    document.title = `${C.title} — MY CHRISTMAS`;
    const m = document.createElement("meta"); m.name = "description"; m.content = C.sub; m.setAttribute(`data-legal-${slug}`, "1"); document.head.appendChild(m);
    return () => { document.title = prev; m.remove(); };
  }, [C.title, C.sub, slug]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-xmas-navy text-xmas-snow" data-testid={testid}>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-xmas-gold/10 bg-xmas-navy/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid={`${testid}-brand`}><TreePine className="h-5 w-5 text-xmas-gold sm:h-6 sm:w-6" /><span className="font-display text-[13px] font-bold tracking-widest sm:text-lg">MY CHRISTMAS</span></Link>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <LanguageSelector compact />
            <Link to="/" data-testid={`${testid}-back`} className="flex items-center gap-1 whitespace-nowrap rounded-full border border-xmas-gold/40 px-3 py-1.5 text-xs transition hover:border-xmas-gold hover:text-xmas-gold sm:px-4 sm:py-2 sm:text-sm"><ArrowLeft className="h-3.5 w-3.5" />{L.back}</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden pt-32 pb-10 sm:pt-40 sm:pb-14">
        <StarField count={40} /><SnowLayer />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-xmas-gold/40 bg-xmas-navy/60"><ScrollText className="h-7 w-7 text-xmas-gold" /></span>
          <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[1.05] sm:text-5xl lg:text-6xl" data-testid={`${testid}-title`}>{C.title}</h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-xmas-champagne md:text-lg" data-testid={`${testid}-sub`}>{C.sub}</p>
          <p className="mx-auto mt-5 inline-block rounded-full border border-xmas-gold/25 bg-xmas-gold/[0.06] px-4 py-1.5 text-[11px] uppercase tracking-widest text-xmas-gold/85" data-testid={`${testid}-notice`}>{C.notice}</p>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        {C.sections.map(([title, text, link], i) => (
          <section key={title} className="border-t border-xmas-gold/10 py-7 sm:py-8" data-testid={`${testid}-section-${i + 1}`}>
            <h2 className="font-display text-xl font-bold leading-tight text-xmas-snow sm:text-2xl"><span className="mr-3 font-display text-xmas-gold/50">{String(i + 1).padStart(2, "0")}</span>{title}</h2>
            <p className="mt-3 text-base leading-relaxed text-xmas-slate">{text}{link === "refund" && <> <Link to="/refund-policy" data-testid={`${testid}-refund-link`} className="font-medium text-xmas-gold underline-offset-4 hover:underline">{L.refund} →</Link></>}</p>
          </section>
        ))}
        <p className="mt-6 text-xs text-xmas-slate/70" data-testid={`${testid}-updated`}>{C.updated}</p>
      </div>

      <footer className="border-t border-xmas-gold/10 py-10 text-center" data-testid={`${testid}-footer`}>
        <p className="font-display text-lg tracking-widest">MY CHRISTMAS</p>
        <p className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-xmas-slate">
          <Link to="/refund-policy" className="underline-offset-4 hover:text-xmas-gold hover:underline" data-testid={`${testid}-footer-refund`}>{L.refund}</Link><span className="text-xmas-gold/40">|</span>
          <Link to="/terms" className="underline-offset-4 hover:text-xmas-gold hover:underline" data-testid={`${testid}-footer-terms`}>{L.terms}</Link><span className="text-xmas-gold/40">|</span>
          <Link to="/privacy" className="underline-offset-4 hover:text-xmas-gold hover:underline" data-testid={`${testid}-footer-privacy`}>{L.privacy}</Link>
        </p>
      </footer>
    </div>
  );
}
